// M16 KPI — içe aktarma (Excel/PDF) grid'i, eşleştirme satırı/sonucu, eşleştirme hafızası
// (K19) ve AI Kontrolü sonucu (§9.1, K17). Sabitler `kpi.ts`'te (tek kaynak).
import { z } from "zod";
import { KPI_BREAKDOWN_ROLLUPS, KPI_MAPPING_ACTIONS } from "./kpi.js";

// ---- Çıkarılan grid (Excel VE PDF ortak çıktısı; Eng 10 kolon modeli) ----
export const KPI_IMPORT_COLUMN_KINDS = [
  "label",
  "unit",
  "year",
  "entity",
  "ignore",
  "description",
  "requirements",
] as const;
export type KpiImportColumnKind = (typeof KPI_IMPORT_COLUMN_KINDS)[number];

export const KpiImportColumnSchema = z.object({
  index: z.number().int(),
  kind: z.enum(KPI_IMPORT_COLUMN_KINDS),
  /** kind=year */
  year: z.number().int().optional(),
  /** kind=entity (GROK düzeni) */
  entityName: z.string().optional(),
  /**
   * K13 (Finar, çok bloklu sayfa) — sanal sütun: aynı Excel sütunu alt tablolarda farklı yıla
   * karşılık geliyorsa (blok içinde yıl başlığı kayıyor) ikinci yıl `index ≥ sayfa genişliği` olan
   * sanal sütuna yazılır; kaynak konumu (kanıt bağı / hücre adresi) bu alandaki gerçek sütundur.
   */
  sourceIndex: z.number().int().min(0).optional(),
});
export type KpiImportColumn = z.infer<typeof KpiImportColumnSchema>;

export const KpiImportRowSchema = z.object({
  /** Stabil kimlik "<sheetIdx>:<rowIdx>" (Eng 10). */
  rowId: z.string(),
  label: z.string(),
  depth: z.number().int().min(0),
  unitHint: z.string().optional(),
  /** Ham hücreler — `parseCell` BE'de uygulanır. */
  cells: z.array(z.unknown()),
});
export type KpiImportRow = z.infer<typeof KpiImportRowSchema>;

export const KPI_IMPORT_SHEET_KINDS = ["data", "notes"] as const;
export type KpiImportSheetKind = (typeof KPI_IMPORT_SHEET_KINDS)[number];

export const KpiImportSheetSchema = z.object({
  name: z.string(),
  columns: z.array(KpiImportColumnSchema),
  rows: z.array(KpiImportRowSchema),
  /**
   * P2-8 (gerçek LLM turu): çıkarım bu sayfayı finansal tablo/bilanço benzeri buldu (sayfa adı ya
   * da etiketlerde mali tablo sözlüğü) — UI "Bu sayfayı atla" önerir; karar kullanıcıda.
   */
  suggestedSkip: z.boolean().optional(),
  /** `suggestedSkip` gerekçesi (kısa, görüntü). */
  skipHint: z.string().optional(),
  /**
   * TODO-KPI-25 — PDF yolunda tablonun belgede kapladığı MUTLAK sayfa aralığı (1 tabanlı; pencere
   * ofseti sunucuda uygulanır). Excel/Word yollarında yok.
   */
  pageStart: z.number().int().positive().optional(),
  pageEnd: z.number().int().positive().optional(),
  /**
   * K13 (Finar) — sayfa türü; alan yoksa `data`. `notes` = yıl başlığı olmayan, uzun metin ağırlıklı
   * açıklama/kıyaslama sayfası: iş hazır olurken otomatik atlanır (kullanıcı geri açabilir).
   */
  kind: z.enum(KPI_IMPORT_SHEET_KINDS).optional(),
  /** K13 — yan yana çok bloklu sayfada algılanan blok sayısı (≥2; tek bloklu sayfada alan yok). */
  blockCount: z.number().int().min(2).optional(),
});
export type KpiImportSheet = z.infer<typeof KpiImportSheetSchema>;

export const KpiImportGridSchema = z.object({
  sheets: z.array(KpiImportSheetSchema),
});
export type KpiImportGrid = z.infer<typeof KpiImportGridSchema>;

// ---- Eşleştirme satırı / sonucu ----
/**
 * Kırılım önerisi (spec §7.1, `action: "create_breakdown"`): üst gösterge (SET kodu) altında
 * `dimension` grubunun `member` üyesi. `complete`/`rollup` yalnız grup YENİyse anlamlıdır (öneri
 * aşaması dosyadan çıkarır; yoksa plan varsayılanı: tam, üstte hesap grubu yoksa toplanır).
 */
export const KpiMappingBreakdownSchema = z.object({
  parentCode: z.string().min(1).max(64),
  dimension: z.string().min(1).max(40),
  groupLabel: z.string().max(200),
  member: z.string().min(1).max(64),
  memberLabel: z.string().min(1).max(200),
  complete: z.boolean().optional(),
  rollup: z.enum(KPI_BREAKDOWN_ROLLUPS).optional(),
});
export type KpiMappingBreakdown = z.infer<typeof KpiMappingBreakdownSchema>;

export const KpiMappingRowSchema = z.object({
  rowId: z.string(),
  /** Yalnız görüntü. */
  rowLabel: z.string(),
  action: z.enum(KPI_MAPPING_ACTIONS),
  /** action=map */
  targetCode: z.string().optional(),
  targetMetricId: z.string().uuid().optional(),
  /** LLM önerisi güveni. */
  confidence: z.number().min(0).max(1).optional(),
  /** K19: hafızadan geldi. */
  memoryHit: z.boolean().optional(),
  note: z.string().optional(),
  /** action=create_breakdown — üst gösterge + boyut + üye. */
  breakdown: KpiMappingBreakdownSchema.optional(),
});
export type KpiMappingRow = z.infer<typeof KpiMappingRowSchema>;

export const KpiYearSchema = z.number().int().min(1990).max(2100);

/**
 * Eşleştirme varsayılanları (B-02/B-03): yıl kolonu olmayan dosyada hedef yıl, işletme kolonu
 * olmayan dosyada grup müşterideki hedef işletme. `kpi_import_jobs.mapping_draft` JSON'unda taşınır,
 * migration gerekmez. İkisi de yokken sunucu sessizce cari yıla / ilk işletmeye YAZMAZ.
 *
 * P2-6 (gerçek LLM turu): yıl SAYFA bazlı seçilebilir — `defaultYearBySheet` anahtarı grid'deki
 * sayfa sırası (`grid.sheets` dizi indeksi, string); yoksa global `defaultYear` (geriye uyumlu).
 * P2-8: `skippedSheets` (aynı indeks) plan/önizleme/commit'te tamamen düşer — bilanço gibi ilgisiz
 * sayfalar aktarımı kilitlemez; eşleştirme önerisi yine tüm sayfalara koşar (karar sonradan).
 */
export const KpiMappingDefaultsSchema = z.object({
  defaultYear: KpiYearSchema.optional(),
  defaultEntityId: z.string().uuid().optional(),
  defaultYearBySheet: z.record(z.string().regex(/^\d+$/u), KpiYearSchema).optional(),
  skippedSheets: z.array(z.number().int().min(0)).max(500).optional(),
});
export type KpiMappingDefaults = z.infer<typeof KpiMappingDefaultsSchema>;

export const KpiMappingResultSchema = z
  .object({
    rows: z.array(KpiMappingRowSchema),
    /** Sheet adı → kolon modeli (kullanıcı Adım 2'de düzenler). */
    columns: z.record(z.string(), z.array(KpiImportColumnSchema)),
    /** P1-1: öneri aşamasının kullanıcıya görünür uyarıları ("N satır için model öneri vermedi"). */
    warnings: z.array(z.string()).optional(),
  })
  .merge(KpiMappingDefaultsSchema);
export type KpiMappingResult = z.infer<typeof KpiMappingResultSchema>;

// ---- Eşleştirme hafızası (kpi_mapping_memory — K19, E8) ----
// "reported" (B-05): türetilmiş hedefe raporlanan değer kararı da hatırlanır — "map"a indirgenmez.
export const KPI_MAPPING_MEMORY_ACTIONS = ["map", "skip", "create", "reported"] as const;
export type KpiMappingMemoryAction = (typeof KPI_MAPPING_MEMORY_ACTIONS)[number];

export const KpiMappingMemorySchema = z.object({
  id: z.string().uuid(),
  /** null = firma geneli. */
  clientId: z.string().uuid().nullable(),
  /** `normalizeMappingLabel` çıktısı. */
  normalizedLabel: z.string(),
  action: z.enum(KPI_MAPPING_MEMORY_ACTIONS),
  metricId: z.string().uuid().nullable(),
  useCount: z.number().int(),
  lastUsedAt: z.string(),
  lastUsedBy: z.string().uuid().nullable(),
});
export type KpiMappingMemory = z.infer<typeof KpiMappingMemorySchema>;

// ---- AI Kontrolü sonucu (kpi_ai_checks — §9.1) ----
export const KPI_AI_CHECK_KEYS = [
  "required_fields",
  "formula_applied",
  "unit_check",
  "period_check",
  "anomaly_scan",
  "evidence_check",
] as const;
export type KpiAiCheckKey = (typeof KPI_AI_CHECK_KEYS)[number];
export const KPI_AI_WARNING_RULES = [
  "unit_mismatch",
  "anomaly_jump",
  "zero_denominator",
  "missing_ref",
  "evidence_mismatch",
  "unit_template_unresolved",
  // Kırılım (spec §3.2 / §18 DR5): grup toplamı ≠ üst değer (tolerans 0) / grupsuz alt satırlı gösterge
  "breakdown_mismatch",
  "legacy_metric_children",
] as const;
export type KpiAiWarningRule = (typeof KPI_AI_WARNING_RULES)[number];

export const KpiAiCheckResultSchema = z.object({
  checklist: z.array(
    z.object({
      key: z.enum(KPI_AI_CHECK_KEYS),
      ok: z.boolean(),
      note: z.string().optional(),
    }),
  ),
  warnings: z.array(
    z.object({
      rule: z.enum(KPI_AI_WARNING_RULES),
      indicatorCode: z.string(),
      year: z.number().int().optional(),
      entityId: z.string().uuid().optional(),
      message: z.string(),
    }),
  ),
  evidenceFindings: z.array(z.unknown()).default([]),
  checkedAt: z.string(),
});
export type KpiAiCheckResult = z.infer<typeof KpiAiCheckResultSchema>;
